import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';
import { getAccessToken } from './auth';

/**
 * Polls GET /files/{fileId}/progress every 15 seconds while `enabled` is true.
 *
 * - Fires one immediate poll on mount (so the bar isn't blank for 15s).
 * - Stops polling and calls `onDone(snapshot)` exactly once when the snapshot
 *   first reports `status === 'done'`.
 * - Transient errors (network / 5xx) keep the previous snapshot on screen and
 *   are surfaced via `lastError` (FR-003).
 * - 401 / 403 / 404 invoke `onError(err)` so the parent can run its existing
 *   logout/redirect flow.
 * - Cleans up: clears the interval and aborts the in-flight request.
 *
 * Returns `{ snapshot, lastError, isPolling }`.
 */
export const POLL_INTERVAL_MS = 15000;

const isAuthOrShape = (err) => {
  const status = err?.response?.status;
  return status === 401 || status === 403 || status === 404;
};

const isAbort = (err) =>
  err?.name === 'CanceledError' || err?.name === 'AbortError' || axios.isCancel?.(err);

export default function useProgressPolling(projectId, enabled, callbacks = {}) {
  const { onDone, onError, isDone, intervalMs = POLL_INTERVAL_MS } = callbacks;
  if (process.env.NODE_ENV !== 'production' && typeof isDone !== 'function') {
    // eslint-disable-next-line no-console
    console.warn('useProgressPolling: no isDone predicate supplied — polling will never stop');
  }
  const _isDone = useMemo(
    () => (typeof isDone === 'function' ? isDone : () => false),
    [isDone],
  );
  const [snapshot, setSnapshot] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const onDoneRef = useRef(onDone);
  const onErrorRef = useRef(onError);
  const isDoneRef = useRef(_isDone);
  const doneFiredRef = useRef(false);

  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { isDoneRef.current = _isDone; }, [_isDone]);

  useEffect(() => {
    if (!projectId || !enabled) {
      setIsPolling(false);
      return undefined;
    }

    doneFiredRef.current = false;
    let cancelled = false;
    let intervalId = null;
    let controller = null;
    setIsPolling(true);

    const stop = () => {
      if (intervalId) clearInterval(intervalId);
      setIsPolling(false);
    };

    const handleSuccess = (data) => {
      setSnapshot(data);
      setLastError(null);
      if (!isDoneRef.current(data) || doneFiredRef.current) return;
      doneFiredRef.current = true;
      stop();
      onDoneRef.current?.(data);
    };

    const handleFailure = (err) => {
      if (cancelled || isAbort(err)) return;
      setLastError(err);
      if (!isAuthOrShape(err)) return;
      stop();
      onErrorRef.current?.(err);
    };

    const poll = async () => {
      if (cancelled || doneFiredRef.current) return;
      controller = new AbortController();
      try {
        const token = getAccessToken();
        const res = await axios.get(getApiUrl(`projects/${projectId}/progress`), {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) handleSuccess(res.data);
      } catch (err) {
        handleFailure(err);
      }
    };

    poll();
    intervalId = setInterval(poll, intervalMs);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (controller) controller.abort();
      setIsPolling(false);
    };
  }, [projectId, enabled, intervalMs]);

  return { snapshot, lastError, isPolling };
}
